import { Component, ElementRef, OnInit, ViewChild, Optional, Output, EventEmitter } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { FirebaseTSFirestore } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSStorage } from 'firebasets/firebasetsStorage/firebaseTSStorage';
import { FirebaseTSApp } from 'firebasets/firebasetsApp/firebaseTSApp';
import { MatSnackBar } from '@angular/material/snack-bar'; // Pour afficher le message de succès
import { NavigationStart, Router } from '@angular/router';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';

@Component({
  selector: 'app-create-post',
  templateUrl: './create-post.component.html',
  styleUrls: ['./create-post.component.css']
})
export class CreatePostComponent implements OnInit {
  isPostButtonEnabled: boolean = false;

  @ViewChild('commentInput') commentInputRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('audioPlayer') audioPlayer: ElementRef;
  @ViewChild('videoElement') videoElement: ElementRef;
  @ViewChild('canvasElement') canvasElement: ElementRef;

  auth = new FirebaseTSAuth();
  firestore = new FirebaseTSFirestore();
  storage = new FirebaseTSStorage();

  selectedImageFiles: File[] = [];
  imagePreviewUrls: string[] = [];

  selectedVideoFile: File | null = null;
  videoPreviewUrl: string | ArrayBuffer | null = null;

  audioRecorder: MediaRecorder;
  audioChunks: any[] = [];
  audioBlobUrl: string | null = null;
  isRecording: boolean = false;
  recordingInProgress: boolean = false;
  recordingTime: number = 0;
  timerInterval: any;
  isRecordedAudio: boolean = false;
  audioDuration: number = 0;

  isLoading = false; // Pour contrôler l'affichage du chargement

  private navigationSubscription: any;

  // Variables pour gérer l'affichage des options de fichiers
  fileOptionsVisible: boolean = false;

  @Output() postCreated = new EventEmitter<void>();

  isMobile: boolean = false;

  isCameraActive: boolean = false;
  hasMultipleCameras: boolean = false;
  currentStream: MediaStream | null = null;
  facingMode: 'user' | 'environment' = 'environment';

  constructor(
    @Optional() private dialogRef: MatDialogRef<CreatePostComponent>,
    @Optional() private bottomSheetRef: MatBottomSheetRef<CreatePostComponent>,
    private snackBar: MatSnackBar,   // Injecter Location
    private router: Router,      // Injecter le Router pour écouter les événements de navigation
  ) { }

  ngOnInit(): void {
    // Intercepter l'événement du bouton retour ou tout changement de navigation
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Fermer la fenêtre modale si une navigation arrière est détectée
        this.closeDialog();
      }
    });
    document.addEventListener('click', this.closeMenu.bind(this));

    // Détecte si c'est un appareil mobile
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  ngOnDestroy(): void {
    // Nettoyer l'abonnement pour éviter les fuites de mémoire
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Nettoyage de l'événement pour éviter les fuites de mémoire
    document.removeEventListener('click', this.closeMenu.bind(this));
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    this.closeCamera();
  }

  // ... autres propriétés et méthodes

  isMenuOpen: boolean = false;

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  // Fermer le menu lorsque l'utilisateur clique en dehors
  closeMenu() {
    this.isMenuOpen = false;
  }

  async selectFile(type: string) {
    this.fileOptionsVisible = false;
    const input = document.createElement('input');
    input.type = 'file';

    if (type === 'image') {
      input.accept = 'image/*';
      input.multiple = true; // Activer la sélection multiple
      
      if (this.selectedVideoFile) {
        this.snackBar.open('Impossible d\'ajouter des photos avec une vidéo', 'OK', { duration: 3000 });
        return;
      }
    } else if (type === 'video') {
      if (this.selectedImageFiles.length > 0 || this.selectedVideoFile || this.audioBlobUrl) {
        this.snackBar.open('Une seule vidéo peut être ajoutée', 'OK', { duration: 3000 });
        return;
      }
      input.accept = 'video/*';
    } else if (type === 'audio') {
      if (this.selectedVideoFile || this.audioBlobUrl) {
        this.snackBar.open('Un seul fichier audio peut être ajouté', 'OK', { duration: 3000 });
        return;
      }
      input.accept = 'audio/*';
    }

    input.onchange = async (event: any) => {
      const files = event.target.files;
      if (files && type === 'image') {
        // Vérifier si l'ajout des nouveaux fichiers ne dépasse pas la limite de 3
        if (this.selectedImageFiles.length + files.length > 3) {
          this.snackBar.open('Maximum 3 photos autorisées', 'OK', { duration: 3000 });
          return;
        }
        
        // Traiter chaque fichier sélectionné
        for (let i = 0; i < files.length; i++) {
          if (this.selectedImageFiles.length < 3) {
            await this.onImageSelected(files[i]);
          }
        }
      } else if (files && files[0]) {
        if (type === 'video') {
          this.onVideoSelected(files[0]);
        } else if (type === 'audio') {
          this.isRecordedAudio = false;
          this.onAudioSelected(files[0]);
        }
      }
    };
    input.click();
  }

  async onImageSelected(file: File) {
    if (file.type.split('/')[0] !== 'image') {
      this.snackBar.open('Veuillez sélectionner une image valide', 'OK', { duration: 3000 });
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.snackBar.open('L\'image ne doit pas dépasser 5MB', 'OK', { duration: 3000 });
      return;
    }

    try {
      const compressedFile = await this.compressImage(file);
      this.selectedImageFiles.push(compressedFile);
      const url = await this.getFileUrl(compressedFile);
      this.imagePreviewUrls.push(url);
    } catch (error) {
      console.error('Erreur lors de la compression de l\'image:', error);
      this.snackBar.open('Erreur lors du traitement de l\'image', 'OK', { duration: 3000 });
    }
  }

  removeImage(index: number) {
    this.selectedImageFiles.splice(index, 1);
    this.imagePreviewUrls.splice(index, 1);
  }

  async onPostClick() {
    if (!this.commentInputRef?.nativeElement.value && !this.selectedImageFiles.length && !this.selectedVideoFile && !this.audioBlobUrl) {
      return;
    }

    try {
      this.isLoading = true;
      const imageUrls: string[] = [];
      const videoUrls: string[] = [];
      const audioUrls: string[] = [];
      const uploadPromises = [];

      // Upload des images
      for (const imageFile of this.selectedImageFiles) {
        const compressedImage = await this.compressImage(imageFile);
        const uniqueFileName = `${Date.now()}_${Math.random().toString(36).substring(2)}_${imageFile.name}`;
        const imageUploadPromise = new Promise<void>((resolve, reject) => {
          this.storage.upload({
            uploadName: "upload",
            path: ["Posts", "Images", uniqueFileName],
            data: {
              data: compressedImage
            },
            onComplete: (downloadUrl) => {
              imageUrls.push(downloadUrl);
              resolve();
            },
            onFail: (error) => reject(error)
          });
        });
        uploadPromises.push(imageUploadPromise);
      }

      // Upload de la vidéo si présente
      if (this.selectedVideoFile) {
        const uniqueFileName = `${Date.now()}_${Math.random().toString(36).substring(2)}_${this.selectedVideoFile.name}`;
        const videoUploadPromise = new Promise<void>((resolve, reject) => {
          this.storage.upload({
            uploadName: "upload",
            path: ["Posts", "Videos", uniqueFileName],
            data: {
              data: this.selectedVideoFile
            },
            onComplete: (downloadUrl) => {
              videoUrls.push(downloadUrl);
              resolve();
            },
            onFail: (error) => reject(error)
          });
        });
        uploadPromises.push(videoUploadPromise);
      }

      // Upload de l'audio si présent
      if (this.audioBlobUrl) {
        const audioBlob = await fetch(this.audioBlobUrl).then(r => r.blob());
        const uniqueFileName = `audio_${Date.now()}_${Math.random().toString(36).substring(2)}.wav`;
        const audioFile = new File([audioBlob], uniqueFileName, { type: 'audio/wav' });
        const audioUploadPromise = new Promise<void>((resolve, reject) => {
          this.storage.upload({
            uploadName: "upload",
            path: ["Posts", "Audios", uniqueFileName],
            data: {
              data: audioFile
            },
            onComplete: (downloadUrl) => {
              audioUrls.push(downloadUrl);
              resolve();
            },
            onFail: (error) => reject(error)
          });
        });
        uploadPromises.push(audioUploadPromise);
      }

      // Attendre que tous les uploads soient terminés
      await Promise.all(uploadPromises);

      // Déterminer le type de post
      let postType = 'text';
      if (this.selectedImageFiles.length > 0 && this.audioBlobUrl) {
        postType = 'audio-image';
      } else if (this.selectedImageFiles.length > 0) {
        postType = 'image';
      } else if (this.selectedVideoFile) {
        postType = 'video';
      } else if (this.audioBlobUrl) {
        postType = 'audio';
      }

      // Créer le post avec toutes les URLs
      const post: any = {
        postId: this.firestore.genDocId(),
        comment: this.commentInputRef.nativeElement.value,
        creatorId: this.auth.getAuth().currentUser?.uid,
        imageUrls: [],
        imageUrl: "",
        videoUrl: "",
        audioUrl: "",
        timestamp: FirebaseTSApp.getFirestoreTimestamp(),
        type: postType,
        likes: [],
        comments: [],
        shares: [],
        lenali: true
      };

      // Gérer les URLs des images
      if (imageUrls.length > 0) {
        post.imageUrls = imageUrls;
        post.imageUrl = imageUrls[0]; // Pour la compatibilité ascendante
      }

      // Gérer l'URL de la vidéo
      if (videoUrls.length > 0) {
        post.videoUrl = videoUrls[0];
      }

      // Gérer l'URL de l'audio
      if (audioUrls.length > 0) {
        post.audioUrl = audioUrls[0];
      }

      // Créer le post dans Firestore
      await new Promise<void>((resolve, reject) => {
        this.firestore.create({
          path: ["Posts"],
          data: post,
          onComplete: () => {
            resolve();
          },
          onFail: (err) => {
            reject(err);
          }
        });
      });

      this.isLoading = false;
      this.snackBar.open('Publication créée avec succès!', 'Fermer', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      this.postCreated.emit();
      
      if (this.dialogRef) {
        this.dialogRef.close();
      } else if (this.bottomSheetRef) {
        this.bottomSheetRef.dismiss();
      }

    } catch (error) {
      console.error('Erreur lors de la création du post:', error);
      this.isLoading = false;
      this.snackBar.open('Erreur lors de la création de la publication', 'Fermer', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }
  }

  onInputChange() {
    const comment = this.commentInputRef.nativeElement.value.trim();
    const hasMedia = this.selectedImageFiles.length > 0 || !!this.selectedVideoFile || !!this.audioBlobUrl;
    this.isPostButtonEnabled = comment.length > 0 || hasMedia;
  }

  // ... autres méthodes existantes ...

  onVideoSelected(file: File): void {
    if (file.type.split('/')[0] !== 'video') {
      this.snackBar.open('Veuillez sélectionner une vidéo valide', 'OK', { duration: 3000 });
      return;
    }

    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      this.snackBar.open('La vidéo ne doit pas dépasser 100MB', 'OK', { duration: 3000 });
      return;
    }

    this.selectedVideoFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.videoPreviewUrl = reader.result;
    };
    reader.readAsDataURL(file);
  }

  removeVideo() {
    this.selectedVideoFile = null;
    this.videoPreviewUrl = null;
  }

  startRecording() {
    this.recordingInProgress = true;
    this.recordingTime = 0;
    this.timerInterval = setInterval(() => this.recordingTime++, 1000);
    this.isRecordedAudio = true;
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      this.audioRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      this.audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };
      this.audioRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        if (this.audioBlobUrl) {
          URL.revokeObjectURL(this.audioBlobUrl);
        }
        this.audioBlobUrl = URL.createObjectURL(audioBlob);
        this.audioChunks = [];
        this.isRecording = false;
        clearInterval(this.timerInterval);
        
        // Arrêter toutes les pistes du stream
        stream.getTracks().forEach(track => track.stop());
      };
      this.audioRecorder.start();
      this.isRecording = true;
    }).catch((error) => {
      console.error("Erreur lors de la capture de l'audio:", error);
      this.recordingInProgress = false;
      this.isRecordedAudio = false;
    });
  }

  stopRecording() {
    if (this.audioRecorder) {
      this.audioRecorder.stop();
    }
    this.recordingInProgress = false;
    clearInterval(this.timerInterval);
  }

  onAudioLoad() {
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      this.audioPlayer.nativeElement.onloadedmetadata = () => {
        this.audioDuration = this.audioPlayer.nativeElement.duration;
      };
    }
  }

  formatTime(time: number): string {
    if (!time) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  removeAudio() {
    this.audioBlobUrl = null;
    this.audioChunks = [];
    this.stopRecording();
  }

  onAudioSelected(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.audioBlobUrl = e.target?.result as string;
      if (this.audioPlayer && this.audioPlayer.nativeElement) {
        this.audioPlayer.nativeElement.onloadedmetadata = () => {
          this.audioDuration = this.audioPlayer.nativeElement.duration;
        };
      }
    };
    reader.readAsDataURL(file);
  }

  // Méthode pour fermer la fenêtre modale
  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }

  // Méthode pour fermer le bottom sheet
  closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  // ... autres méthodes existantes ...

  async openCamera() {
    if (!this.selectedVideoFile && this.selectedImageFiles.length < 3) {
      try {
        // Si on est dans un bottomSheet, ajuster le style
        if (this.bottomSheetRef) {
          const bottomSheetContainer = document.querySelector('.mat-bottom-sheet-container') as HTMLElement;
          if (bottomSheetContainer) {
            bottomSheetContainer.style.overflow = 'hidden';
            bottomSheetContainer.style.maxHeight = '100vh';
            bottomSheetContainer.style.height = '100vh';
          }
        }

        this.isCameraActive = true;
        await this.startCamera();
      } catch (error) {
        console.error('Erreur lors de l\'accès à la caméra:', error);
        this.snackBar.open('Impossible d\'accéder à la caméra', 'Fermer', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }
    } else {
      this.snackBar.open('Vous ne pouvez pas ajouter plus de photos', 'Fermer', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }
  }

  async startCamera() {
    try {
      // Vérifier la disponibilité de plusieurs caméras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      this.hasMultipleCameras = videoDevices.length > 1;

      // Démarrer le flux vidéo
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facingMode }
      });

      if (this.currentStream) {
        this.currentStream.getTracks().forEach(track => track.stop());
      }

      this.currentStream = stream;
      this.videoElement.nativeElement.srcObject = stream;
    } catch (error) {
      console.error('Erreur lors du démarrage de la caméra:', error);
      throw error;
    }
  }

  async switchCamera() {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    await this.startCamera();
  }

  async capturePhoto() {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    // Définir les dimensions du canvas pour correspondre à la vidéo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Dessiner l'image de la vidéo sur le canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convertir le canvas en blob
    const blob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

    // Ajouter la photo et fermer la caméra
    await this.onImageSelected(file);
    this.closeCamera();
  }

  closeCamera() {
    if (this.currentStream) {
      this.currentStream.getTracks().forEach(track => track.stop());
      this.currentStream = null;
    }

    // Restaurer le style du bottomSheet
    if (this.bottomSheetRef) {
      const bottomSheetContainer = document.querySelector('.mat-bottom-sheet-container') as HTMLElement;
      if (bottomSheetContainer) {
        bottomSheetContainer.style.overflow = '';
        bottomSheetContainer.style.maxHeight = '';
        bottomSheetContainer.style.height = '';
      }
    }

    this.isCameraActive = false;
  }

  // ... reste du code existant ...

  private async getFileUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsDataURL(file);
    });
  }

  private async compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionner si l'image est trop grande
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Convertir en JPEG avec une qualité de 0.8
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Échec de la compression de l\'image'));
              }
            },
            'image/jpeg',
            0.8
          );
        };
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  toggleFileOptions(): void {
    this.fileOptionsVisible = !this.fileOptionsVisible;
  }
}
